"use client";

import { useState, useEffect } from "react";
import Icon from "@/components/ui/AppIcon";

export default function PartnerSignupForm() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    yearsExperience: "",
    certifications: "",
    erpExpertise: [] as string[],
    platformExpertise: [] as string[],
    specializations: [] as string[],
    linkedinUrl: "",
    portfolioUrl: "",
    referenceContact: "",
    motivation: "",
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleMultiSelect = (field: string, value: string) => {
    const currentValues = formData[field as keyof typeof formData] as string[];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    setFormData({ ...formData, [field]: newValues });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock submission - in production this would send to validation queue
    console.log("Partner application submitted:", formData);
    setSubmitted(true);
  };

  if (!isHydrated) {
    return (
      <div className="glass-card rounded-2xl p-8 max-w-4xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-neutral-800 rounded" />
          <div className="h-10 bg-neutral-800 rounded" />
          <div className="h-10 bg-neutral-800 rounded" />
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="glass-card rounded-2xl p-8 max-w-4xl mx-auto text-center">
        <Icon name="CheckCircleIcon" size={64} className="text-emerald-400 mx-auto mb-6" />
        <h2 className="text-3xl font-bold text-white mb-4">
          Application Submitted Successfully
        </h2>
        <p className="text-xl text-neutral-400 mb-6">
          Thank you for your interest in joining our partner architect network.
        </p>
        <div className="glass-card rounded-xl p-6 bg-blue-500/10 border-blue-500/30 max-w-2xl mx-auto">
          <p className="text-neutral-300 mb-4">
            Our expert validation team will review your application within 5-7
            business days. You will receive an email notification once the review
            is complete.
          </p>
          <p className="text-sm text-neutral-500">
            If approved, you&apos;ll receive onboarding materials and access to the
            partner portal.
          </p>
        </div>
      </div>
    );
  }

  const erpOptions = [
    "SAP",
    "Oracle",
    "NetSuite",
    "Workday",
    "Dynamics",
    "Infor",
    "Epicor",
  ];

  const platformOptions = [
    "SAP Integration Suite",
    "MuleSoft",
    "Boomi",
    "Azure Integration",
    "AWS Integration",
    "Informatica",
    "SnapLogic",
  ];

  const specializationOptions = [
    "Order-to-Cash",
    "Procure-to-Pay",
    "Supply Chain",
    "Finance & Accounting",
    "HR & Payroll",
    "Manufacturing",
    "E-commerce",
    "API Design",
  ];

  return (
    <form
      onSubmit={handleSubmit}
      className="glass-card rounded-2xl p-8 max-w-4xl mx-auto space-y-8"
    >
      <div className="border-b border-white/10 pb-6">
        <h2 className="text-2xl font-bold text-white mb-2">
          Partner Architect Application
        </h2>
        <p className="text-neutral-400">
          All applications are reviewed by our expert team to ensure partnership
          eligibility and quality standards.
        </p>
      </div>

      {/* Personal Information */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-white">
          Personal Information
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
            >
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
            >
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="company"
              className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
            >
              Company/Firm *
            </label>
            <input
              type="text"
              id="company"
              name="company"
              required
              value={formData.company}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label
              htmlFor="role"
              className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
            >
              Current Role *
            </label>
            <input
              type="text"
              id="role"
              name="role"
              required
              value={formData.role}
              onChange={handleChange}
              placeholder="e.g., Integration Architect, Solutions Architect"
              className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Experience & Credentials */}
      <div className="space-y-6 border-t border-white/10 pt-6">
        <h3 className="text-lg font-semibold text-white">
          Experience & Credentials
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="yearsExperience"
              className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
            >
              Years of Integration Experience *
            </label>
            <select
              id="yearsExperience"
              name="yearsExperience"
              required
              value={formData.yearsExperience}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="">Select experience level</option>
              <option value="3-5">3-5 years</option>
              <option value="5-10">5-10 years</option>
              <option value="10-15">10-15 years</option>
              <option value="15+">15+ years</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="certifications"
              className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
            >
              Certifications
            </label>
            <input
              type="text"
              id="certifications"
              name="certifications"
              value={formData.certifications}
              onChange={handleChange}
              placeholder="e.g., SAP Certified, MuleSoft Certified"
              className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            />
          </div>
        </div>
      </div>

      {/* Technical Expertise */}
      <div className="space-y-6 border-t border-white/10 pt-6">
        <h3 className="text-lg font-semibold text-white">Technical Expertise</h3>

        <div>
          <label className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-3">
            ERP Platforms (Select all that apply) *
          </label>
          <div className="flex flex-wrap gap-3">
            {erpOptions.map((erp) => (
              <button
                key={erp}
                type="button"
                onClick={() => handleMultiSelect("erpExpertise", erp)}
                className={`px-4 py-2 rounded-full border text-sm transition-all ${
                  formData.erpExpertise.includes(erp)
                    ? "bg-blue-500/20 border-blue-500 text-blue-400" :"bg-neutral-800 border-white/10 text-neutral-300 hover:border-blue-500/30"
                }`}
              >
                {erp}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-3">
            Integration Platforms (Select all that apply) *
          </label>
          <div className="flex flex-wrap gap-3">
            {platformOptions.map((platform) => (
              <button
                key={platform}
                type="button"
                onClick={() => handleMultiSelect("platformExpertise", platform)}
                className={`px-4 py-2 rounded-full border text-sm transition-all ${
                  formData.platformExpertise.includes(platform)
                    ? "bg-blue-500/20 border-blue-500 text-blue-400" :"bg-neutral-800 border-white/10 text-neutral-300 hover:border-blue-500/30"
                }`}
              >
                {platform}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-3">
            Specializations (Select all that apply) *
          </label>
          <div className="flex flex-wrap gap-3">
            {specializationOptions.map((spec) => (
              <button
                key={spec}
                type="button"
                onClick={() => handleMultiSelect("specializations", spec)}
                className={`px-4 py-2 rounded-full border text-sm transition-all ${
                  formData.specializations.includes(spec)
                    ? "bg-blue-500/20 border-blue-500 text-blue-400" :"bg-neutral-800 border-white/10 text-neutral-300 hover:border-blue-500/30"
                }`}
              >
                {spec}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Professional Links */}
      <div className="space-y-6 border-t border-white/10 pt-6">
        <h3 className="text-lg font-semibold text-white">Professional Links</h3>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="linkedinUrl"
              className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
            >
              LinkedIn Profile *
            </label>
            <input
              type="url"
              id="linkedinUrl"
              name="linkedinUrl"
              required
              value={formData.linkedinUrl}
              onChange={handleChange}
              placeholder="https://linkedin.com/in/yourprofile"
              className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label
              htmlFor="portfolioUrl"
              className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
            >
              Portfolio/Website
            </label>
            <input
              type="url"
              id="portfolioUrl"
              name="portfolioUrl"
              value={formData.portfolioUrl}
              onChange={handleChange}
              placeholder="https://yourportfolio.com"
              className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="referenceContact"
            className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
          >
            Professional Reference (Optional)
          </label>
          <input
            type="text"
            id="referenceContact"
            name="referenceContact"
            value={formData.referenceContact}
            onChange={handleChange}
            placeholder="Name and contact information"
            className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Motivation */}
      <div className="space-y-6 border-t border-white/10 pt-6">
        <h3 className="text-lg font-semibold text-white">Application Statement</h3>

        <div>
          <label
            htmlFor="motivation"
            className="block text-sm font-semibold text-neutral-400 uppercase tracking-wide mb-2"
          >
            Why do you want to join our partner network? *
          </label>
          <textarea
            id="motivation"
            name="motivation"
            required
            rows={6}
            value={formData.motivation}
            onChange={handleChange}
            placeholder="Tell us about your integration experience, what you can bring to the network, and why you're interested in partnership..."
            className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Validation Notice */}
      <div className="glass-card rounded-xl p-6 bg-amber-500/10 border-amber-500/30">
        <div className="flex items-start gap-3">
          <Icon name="ExclamationTriangleIcon" size={24} className="text-amber-400 flex-shrink-0 mt-1" />
          <div>
            <h4 className="text-white font-semibold mb-2">Expert Validation Required</h4>
            <p className="text-sm text-neutral-300">
              All applications undergo thorough review by our senior integration
              architects. We validate credentials, experience, and technical
              expertise to maintain the quality of our partner network. Review
              typically takes 5-7 business days.
            </p>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-center pt-6">
        <button
          type="submit"
          className="px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full text-lg font-medium hover:scale-105 transition-transform flex items-center gap-2"
        >
          Submit Application
          <Icon name="ArrowRightIcon" size={20} />
        </button>
      </div>
    </form>
  );
}